import { get, getNum, getStr, isEmail, isPhone, isYes } from '@shared';

// ---------- COVERSHEET INFORMATION RULES ---------------------------

// R1: Partner case name required when partner case selected
const rulePartnerCaseNameRequired: Rule = {
  id: 'partner.case.name.required',
  when: form => isYes(get(form, 'isPartnerCase')),
  check: (form): RuleResult => {
    const firstName = getStr(form, 'partnerCaseNameFirst');
    const lastName = getStr(form, 'partnerCaseNameLast');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!firstName) {
      errs.push('Partner case first name is required when case is submitted with partner.');
      refs.push('partnerCaseNameFirst');
    }

    if (!lastName) {
      errs.push('Partner case last name is required when case is submitted with partner.');
      refs.push('partnerCaseNameLast');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PARTNER_CASE_NAME_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'isPartnerCase'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R2: Financial professional email format validation
const ruleFinancialProfessionalEmailFormat: Rule = {
  id: 'financial.professional.email.format',
  check: (form): RuleResult => {
    const email = getStr(form, 'financialProfessionalAltEmail');

    if (email && !isEmail(email)) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'FINANCIAL_PROFESSIONAL_EMAIL_INVALID',
        message: 'Financial professional alternate email address format looks invalid.',
        refs: ['financialProfessionalAltEmail'],
        category: 'formatting',
      };
    }

    return { ok: true };
  },
};

// ---------- PROPOSED INSURED RULES ----------------------------------

// R3: Mailing address required when different from home address
const ruleMailingAddressRequired: Rule = {
  id: 'mailing.address.required',
  when: form => isYes(get(form, 'mailAddressDifferent')),
  check: (form): RuleResult => {
    const street = getStr(form, 'mailingStreet');
    const city = getStr(form, 'mailingCity');
    const state = getStr(form, 'mailingState');
    const zip = getStr(form, 'mailingZip');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!street) {
      errs.push('Mailing street address is required.');
      refs.push('mailingStreet');
    }

    if (!city) {
      errs.push('Mailing city is required.');
      refs.push('mailingCity');
    }

    if (!state) {
      errs.push('Mailing state is required.');
      refs.push('mailingState');
    }

    if (!zip) {
      errs.push('Mailing zip code is required.');
      refs.push('mailingZip');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'MAILING_ADDRESS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'mailAddressDifferent'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R4: Email and phone format validation for proposed insured
const ruleProposedInsuredContactFormat: Rule = {
  id: 'proposed.insured.contact.format',
  check: (form): RuleResult => {
    const email = getStr(form, 'email');
    const primaryPhone = getStr(form, 'primaryPhone');
    const cellPhone = getStr(form, 'cellPhone');
    const errs: string[] = [];
    const refs: string[] = [];

    if (email && !isEmail(email)) {
      errs.push('Email address format looks invalid.');
      refs.push('email');
    }

    if (primaryPhone && !isPhone(primaryPhone)) {
      errs.push('Primary phone number format looks invalid.');
      refs.push('primaryPhone');
    }

    if (cellPhone && !isPhone(cellPhone)) {
      errs.push('Cell phone number format looks invalid.');
      refs.push('cellPhone');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CONTACT_FORMAT_INVALID',
        message: errs.join(' '),
        refs,
        category: 'formatting',
      };
    }

    return { ok: true };
  },
};

// R5: Spouse/Partner name required when married
const ruleSpouseNameRequired: Rule = {
  id: 'spouse.name.required',
  when: form => isYes(get(form, 'married')),
  check: (form): RuleResult => {
    const firstName = getStr(form, 'spousePartnerFirst');
    const lastName = getStr(form, 'spousePartnerLast');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!firstName) {
      errs.push('Spouse/Partner first name is required when married.');
      refs.push('spousePartnerFirst');
    }

    if (!lastName) {
      errs.push('Spouse/Partner last name is required when married.');
      refs.push('spousePartnerLast');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'SPOUSE_NAME_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'married'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- OWNER INFORMATION RULES ---------------------------------

// R6: Owner individual name required when owner type is individual
const ruleOwnerIndividualNameRequired: Rule = {
  id: 'owner.individual.name.required',
  when: form => {
    const ownerType = getStr(form, 'ownerType');
    return ownerType === 'individual' || ownerType === 'individual-other';
  },
  check: (form): RuleResult => {
    const firstName = getStr(form, 'ownerFirst');
    const lastName = getStr(form, 'ownerLast');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!firstName) {
      errs.push('Owner first name is required for individual owner.');
      refs.push('ownerFirst');
    }

    if (!lastName) {
      errs.push('Owner last name is required for individual owner.');
      refs.push('ownerLast');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'OWNER_INDIVIDUAL_NAME_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'ownerType'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R7: Trust/Entity owner name required when owner type is trust or entity
const ruleTrustEntityOwnerNameRequired: Rule = {
  id: 'trust.entity.owner.name.required',
  when: form => {
    const ownerType = getStr(form, 'ownerType');
    return ownerType === 'trust' || ownerType === 'entity';
  },
  check: (form): RuleResult => {
    const trustEntityName = getStr(form, 'trustEntityOwnerName');

    if (!trustEntityName) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'TRUST_ENTITY_NAME_REQUIRED',
        message: 'Trust/Entity name is required when owner type is Trust or Entity.',
        refs: ['trustEntityOwnerName', 'ownerType'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R8: Owner contact validation
const ruleOwnerContactFormat: Rule = {
  id: 'owner.contact.format',
  check: (form): RuleResult => {
    const email = getStr(form, 'ownerEmail');
    const primaryPhone = getStr(form, 'ownerPrimaryPhone');
    const cellPhone = getStr(form, 'ownerCellPhone');
    const errs: string[] = [];
    const refs: string[] = [];

    if (email && !isEmail(email)) {
      errs.push('Owner email address format looks invalid.');
      refs.push('ownerEmail');
    }

    if (primaryPhone && !isPhone(primaryPhone)) {
      errs.push('Owner primary phone number format looks invalid.');
      refs.push('ownerPrimaryPhone');
    }

    if (cellPhone && !isPhone(cellPhone)) {
      errs.push('Owner cell phone number format looks invalid.');
      refs.push('ownerCellPhone');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'OWNER_CONTACT_FORMAT_INVALID',
        message: errs.join(' '),
        refs,
        category: 'formatting',
      };
    }

    return { ok: true };
  },
};

// ---------- BENEFICIARY INFORMATION RULES ---------------------------

// R9: Beneficiary individual name required when type is individual
const ruleBeneficiaryIndividualNameRequired: Rule = {
  id: 'beneficiary.individual.name.required',
  check: (form): RuleResult => {
    const beneficiaries = [
      {
        type: 'beneficiary5Type',
        first: 'beneficiary5IndividualFirstName',
        last: 'beneficiary5IndividualLastName',
        num: '5',
      },
      {
        type: 'beneficiary6Type',
        first: 'beneficiary6NameFirst',
        last: 'beneficiary6NameLast',
        num: '6',
      },
      {
        type: 'beneficiary7Type',
        first: 'beneficiary7NameFirst',
        last: 'beneficiary7NameLast',
        num: '7',
      },
    ];

    const errs: string[] = [];
    const refs: string[] = [];

    for (const ben of beneficiaries) {
      const type = getStr(form, ben.type);
      if (type === 'individual') {
        const firstName = getStr(form, ben.first);
        const lastName = getStr(form, ben.last);

        if (!firstName) {
          errs.push(`Beneficiary #${ben.num} first name is required when type is Individual.`);
          refs.push(ben.first);
        }

        if (!lastName) {
          errs.push(`Beneficiary #${ben.num} last name is required when type is Individual.`);
          refs.push(ben.last);
        }
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'BENEFICIARY_INDIVIDUAL_NAME_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R10: Beneficiary entity/trust name required when type is entity
const ruleBeneficiaryEntityNameRequired: Rule = {
  id: 'beneficiary.entity.name.required',
  check: (form): RuleResult => {
    const beneficiaries = [
      { type: 'beneficiary5Type', entityName: 'beneficiary5TrustName', num: '5' },
      { type: 'beneficiary6Type', entityName: 'beneficiary6EntityName', num: '6' },
      { type: 'beneficiary7Type', entityName: 'beneficiary7EntityName', num: '7' },
    ];

    const errs: string[] = [];
    const refs: string[] = [];

    for (const ben of beneficiaries) {
      const type = getStr(form, ben.type);
      if (type === 'entity' || type === 'trust') {
        const entityName = getStr(form, ben.entityName);

        if (!entityName) {
          errs.push(
            `Beneficiary #${ben.num} entity/trust name is required when type is Entity or Trust.`,
          );
          refs.push(ben.entityName);
        }
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'BENEFICIARY_ENTITY_NAME_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R11: Beneficiary phone format validation
const ruleBeneficiaryPhoneFormat: Rule = {
  id: 'beneficiary.phone.format',
  check: (form): RuleResult => {
    const phones = [
      { field: 'beneficiary5Phone', num: '5' },
      { field: 'beneficiary6Phone', num: '6' },
      { field: 'beneficiary7Phone', num: '7' },
    ];

    const errs: string[] = [];
    const refs: string[] = [];

    for (const p of phones) {
      const phone = getStr(form, p.field);
      if (phone && !isPhone(phone)) {
        errs.push(`Beneficiary #${p.num} phone number format looks invalid.`);
        refs.push(p.field);
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'BENEFICIARY_PHONE_FORMAT_INVALID',
        message: errs.join(' '),
        refs,
        category: 'formatting',
      };
    }

    return { ok: true };
  },
};

// ---------- PAYOR INFORMATION RULES ---------------------------------

// R12: Payor individual name required when name type is individual
const rulePayorIndividualNameRequired: Rule = {
  id: 'payor.individual.name.required',
  when: form => {
    const selection = getStr(form, 'payorSelection');
    const nameType = getStr(form, 'payorNameType');
    return selection === 'other' && nameType === 'individual';
  },
  check: (form): RuleResult => {
    const firstName = getStr(form, 'payorIndividualFirstName');
    const lastName = getStr(form, 'payorIndividualLastName');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!firstName) {
      errs.push('Payor first name is required for individual payor.');
      refs.push('payorIndividualFirstName');
    }

    if (!lastName) {
      errs.push('Payor last name is required for individual payor.');
      refs.push('payorIndividualLastName');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PAYOR_INDIVIDUAL_NAME_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'payorNameType'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R13: Payor entity name required when name type is entity
const rulePayorEntityNameRequired: Rule = {
  id: 'payor.entity.name.required',
  when: form => {
    const selection = getStr(form, 'payorSelection');
    const nameType = getStr(form, 'payorNameType');
    return selection === 'other' && nameType === 'entity';
  },
  check: (form): RuleResult => {
    const entityName = getStr(form, 'payorEntityName');

    if (!entityName) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PAYOR_ENTITY_NAME_REQUIRED',
        message: 'Payor entity name is required when payor type is Entity.',
        refs: ['payorEntityName', 'payorNameType'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R14: Payor address required when selection is other
const rulePayorAddressRequired: Rule = {
  id: 'payor.address.required',
  when: form => getStr(form, 'payorSelection') === 'other',
  check: (form): RuleResult => {
    const street = getStr(form, 'payorAddressStreet');
    const city = getStr(form, 'payorAddressCity');
    const state = getStr(form, 'payorAddressState');
    const zip = getStr(form, 'payorAddressZip');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!street) {
      errs.push('Payor street address is required.');
      refs.push('payorAddressStreet');
    }

    if (!city) {
      errs.push('Payor city is required.');
      refs.push('payorAddressCity');
    }

    if (!state) {
      errs.push('Payor state is required.');
      refs.push('payorAddressState');
    }

    if (!zip) {
      errs.push('Payor zip code is required.');
      refs.push('payorAddressZip');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PAYOR_ADDRESS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'payorSelection'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- EXISTING/PENDING INSURANCE RULES ------------------------

// R15: Existing insurance action details required when stopping payments or reducing benefits
const ruleExistingInsuranceActionDetailsRequired: Rule = {
  id: 'existing.insurance.action.details.required',
  when: form =>
    isYes(get(form, 'existingInsuranceStoppingPayments')) ||
    isYes(get(form, 'existingInsuranceReducingBenefits')),
  check: (form): RuleResult => {
    const details = getStr(form, 'existingInsuranceActionDetails');

    if (!details) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'EXISTING_INSURANCE_ACTION_DETAILS_REQUIRED',
        message:
          'Details are required when stopping payments or reducing benefits on existing insurance.',
        refs: [
          'existingInsuranceActionDetails',
          'existingInsuranceStoppingPayments',
          'existingInsuranceReducingBenefits',
        ],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- PROTECTION AGAINST UNINTENDED LAPSE RULES ---------------

// R16: Third party details required when designate third party selected
const ruleThirdPartyDetailsRequired: Rule = {
  id: 'third.party.details.required',
  when: form => getStr(form, 'unintendedLapseSelection') === 'designate-third-party',
  check: (form): RuleResult => {
    const firstName = getStr(form, 'thirdPartyFirstName');
    const lastName = getStr(form, 'thirdPartyLastName');
    const phone = getStr(form, 'thirdPartyPhoneNumber');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!firstName) {
      errs.push('Third party first name is required.');
      refs.push('thirdPartyFirstName');
    }

    if (!lastName) {
      errs.push('Third party last name is required.');
      refs.push('thirdPartyLastName');
    }

    if (!phone) {
      errs.push('Third party phone number is required.');
      refs.push('thirdPartyPhoneNumber');
    } else if (!isPhone(phone)) {
      errs.push('Third party phone number format looks invalid.');
      refs.push('thirdPartyPhoneNumber');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'THIRD_PARTY_DETAILS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'unintendedLapseSelection'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R17: Third party address required when third party designated
const ruleThirdPartyAddressRequired: Rule = {
  id: 'third.party.address.required',
  when: form => getStr(form, 'unintendedLapseSelection') === 'designate-third-party',
  check: (form): RuleResult => {
    const street = getStr(form, 'thirdPartyAddressStreet');
    const city = getStr(form, 'thirdPartyAddressCity');
    const state = getStr(form, 'thirdPartyAddressState');
    const zip = getStr(form, 'thirdPartyAddressZip');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!street) {
      errs.push('Third party street address is required.');
      refs.push('thirdPartyAddressStreet');
    }

    if (!city) {
      errs.push('Third party city is required.');
      refs.push('thirdPartyAddressCity');
    }

    if (!state) {
      errs.push('Third party state is required.');
      refs.push('thirdPartyAddressState');
    }

    if (!zip) {
      errs.push('Third party zip code is required.');
      refs.push('thirdPartyAddressZip');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'THIRD_PARTY_ADDRESS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'unintendedLapseSelection'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- INFLATION PROTECTION RULES ------------------------------

// R18: Inflation protection compound percentage required when option B selected for Universal Life
const ruleInflationProtectionCompoundPercentageRequired: Rule = {
  id: 'inflation.protection.compound.percentage.required',
  when: form => getStr(form, 'inflationProtectionUniversalLife') === 'option-b',
  check: (form): RuleResult => {
    const percentage = getNum(form, 'inflationProtectionUniversalCompoundPercentage');

    if (!percentage) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'INFLATION_COMPOUND_PERCENTAGE_REQUIRED',
        message:
          'Compound percentage is required when selecting Option B for inflation protection.',
        refs: [
          'inflationProtectionUniversalCompoundPercentage',
          'inflationProtectionUniversalLife',
        ],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- AGENT/FINANCIAL PROFESSIONAL RULES ----------------------

// R19: Agent relationship details required when related to proposed insured
const ruleAgentRelationshipDetailsRequired: Rule = {
  id: 'agent.relationship.details.required',
  when: form => isYes(get(form, 'agentRelatedToProposedInsured')),
  check: (form): RuleResult => {
    const details = getStr(form, 'agentRelationshipDetails');

    if (!details) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'AGENT_RELATIONSHIP_DETAILS_REQUIRED',
        message: 'Relationship details are required when agent is related to proposed insured.',
        refs: ['agentRelationshipDetails', 'agentRelatedToProposedInsured'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R20: English completion explanation required when proposed insured doesn't understand English
const ruleEnglishCompletionExplanationRequired: Rule = {
  id: 'english.completion.explanation.required',
  when: form => {
    const understanding = getStr(form, 'agentEnglishUnderstanding');
    return understanding === 'no';
  },
  check: (form): RuleResult => {
    const explanation = getStr(form, 'agentEnglishCompletionExplanation');

    if (!explanation) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'ENGLISH_COMPLETION_EXPLANATION_REQUIRED',
        message:
          'Explanation is required when proposed insured and owner(s) do not understand English.',
        refs: ['agentEnglishCompletionExplanation', 'agentEnglishUnderstanding'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R21: Agent hierarchy change description required when change indicated
const ruleAgentHierarchyChangeDescriptionRequired: Rule = {
  id: 'agent.hierarchy.change.description.required',
  when: form => isYes(get(form, 'agentHierarchyChange')),
  check: (form): RuleResult => {
    const description = getStr(form, 'agentHierarchyChangeDescription');

    if (!description) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'AGENT_HIERARCHY_CHANGE_DESCRIPTION_REQUIRED',
        message: 'Description is required when hierarchy change is indicated.',
        refs: ['agentHierarchyChangeDescription', 'agentHierarchyChange'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- MEDICAL INFORMATION RULES -------------------------------

// R22: Height and weight sanity checks
const ruleHeightWeightSanity: Rule = {
  id: 'height.weight.sanity',
  check: (form): RuleResult => {
    const heightFeet = getNum(form, 'medicalHeightFeet');
    const heightInches = getNum(form, 'medicalHeightInches');
    const weight = getNum(form, 'medicalWeight');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!Number.isNaN(heightFeet) && (heightFeet < 4 || heightFeet > 7)) {
      errs.push('Height (feet) must be between 4 and 7.');
      refs.push('medicalHeightFeet');
    }

    if (!Number.isNaN(heightInches) && (heightInches < 0 || heightInches > 11)) {
      errs.push('Height (inches) must be between 0 and 11.');
      refs.push('medicalHeightInches');
    }

    if (!Number.isNaN(weight) && (weight < 50 || weight > 500)) {
      errs.push('Weight must be between 50 and 500 pounds.');
      refs.push('medicalWeight');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'HEIGHT_WEIGHT_SANITY',
        message: errs.join(' '),
        refs,
        category: 'data-consistency',
      };
    }

    return { ok: true };
  },
};

// R23: Physician information required
const rulePhysicianInformationRequired: Rule = {
  id: 'physician.information.required',
  check: (form): RuleResult => {
    const name = getStr(form, 'physicianName');
    const phone = getStr(form, 'physicianPhone');
    const errs: string[] = [];
    const refs: string[] = [];

    if (name && !phone) {
      errs.push('Physician phone number is required when physician name is provided.');
      refs.push('physicianPhone', 'physicianName');
    }

    if (phone && !isPhone(phone)) {
      errs.push('Physician phone number format looks invalid.');
      refs.push('physicianPhone');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PHYSICIAN_INFO_INCOMPLETE',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- MEDICAL SUPPLEMENT - INSURABILITY RISK RULES ------------

// R24: Major medical conditions eligibility risk
const ruleMajorConditionsEligibilityRisk: Rule = {
  id: 'major.conditions.eligibility.risk',
  check: (form): RuleResult => {
    const highRiskConditions = [
      { field: 'suppAlzheimersDementia', label: "Alzheimer's disease or dementia" },
      { field: 'suppAls', label: "ALS (Lou Gehrig's disease)" },
      { field: 'suppHuntingtonsDisease', label: "Huntington's disease" },
      { field: 'suppMultipleSclerosis', label: 'Multiple sclerosis' },
      { field: 'suppParkinsonsDisease', label: "Parkinson's disease" },
      { field: 'suppSchizophrenia', label: 'Schizophrenia' },
      { field: 'suppMemoryLossWithMedication', label: 'Memory loss requiring medication' },
      { field: 'suppOrganTransplantNonCornea', label: 'Organ transplant (non-cornea)' },
    ];

    const issues: string[] = [];
    const refs: string[] = [];

    for (const condition of highRiskConditions) {
      if (isYes(get(form, condition.field))) {
        issues.push(`Applicant diagnosed with ${condition.label}`);
        refs.push(condition.field);
      }
    }

    if (issues.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'MAJOR_CONDITION_DECLINE_RISK',
        message: `${issues.map(issue => `• ${issue}`).join('\n')}; this is typically an automatic decline per Lincoln underwriting guidelines.`,
        refs,
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R25: Assistive devices eligibility risk
const ruleAssistiveDevicesEligibilityRisk: Rule = {
  id: 'assistive.devices.eligibility.risk',
  check: (form): RuleResult => {
    const devices = [
      { field: 'suppUsesWheelchair', label: 'wheelchair' },
      { field: 'suppUsesWalker', label: 'walker' },
      { field: 'suppUsesDialysisMachine', label: 'dialysis machine' },
      { field: 'suppUsesOxygen', label: 'oxygen' },
      { field: 'suppUsesCatheter', label: 'catheter' },
    ];

    const issues: string[] = [];
    const refs: string[] = [];

    for (const device of devices) {
      if (isYes(get(form, device.field))) {
        issues.push(`Uses ${device.label}`);
        refs.push(device.field);
      }
    }

    if (issues.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'ASSISTIVE_DEVICES_DECLINE_RISK',
        message: `Applicant uses assistive devices: ${issues.join(', ')}; this is typically an automatic decline per Lincoln underwriting guidelines.`,
        refs,
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R26: ADL disorder eligibility risk
const ruleADLDisorderEligibilityRisk: Rule = {
  id: 'adl.disorder.eligibility.risk',
  check: (form): RuleResult => {
    if (isYes(get(form, 'suppADLDisorder'))) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'ADL_DISORDER_DECLINE_RISK',
        message:
          'Applicant has disorder requiring assistance with ADLs; this is typically an automatic decline per Lincoln underwriting guidelines.',
        refs: ['suppADLDisorder'],
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R27: SSDI eligibility risk
const ruleSSDIEligibilityRisk: Rule = {
  id: 'ssdi.eligibility.risk',
  check: (form): RuleResult => {
    if (isYes(get(form, 'suppSSDI'))) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'SSDI_DECLINE_RISK',
        message:
          'Applicant received Social Security Disability Income in past 5 years; this is typically an automatic decline per Lincoln underwriting guidelines.',
        refs: ['suppSSDI'],
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R28: AIDS diagnosis eligibility risk
const ruleAIDSDiagnosisEligibilityRisk: Rule = {
  id: 'aids.diagnosis.eligibility.risk',
  check: (form): RuleResult => {
    if (isYes(get(form, 'suppAIDSDiagnosis')) || isYes(get(form, 'medicalHIVDiagnosis'))) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'AIDS_HIV_DECLINE_RISK',
        message:
          'Applicant diagnosed with HIV/AIDS; this is typically an automatic decline per Lincoln underwriting guidelines.',
        refs: ['suppAIDSDiagnosis', 'medicalHIVDiagnosis'],
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R29: Recent cancer eligibility risk
const ruleRecentCancerEligibilityRisk: Rule = {
  id: 'recent.cancer.eligibility.risk',
  check: (form): RuleResult => {
    if (isYes(get(form, 'suppCancer3Years'))) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'RECENT_CANCER_RISK',
        message:
          'Applicant had cancer treatment in past 3 years; this may result in decline or postponement per Lincoln underwriting guidelines.',
        refs: ['suppCancer3Years'],
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R30: Multiple falls eligibility risk
const ruleMultipleFallsEligibilityRisk: Rule = {
  id: 'multiple.falls.eligibility.risk',
  check: (form): RuleResult => {
    const twoOrMoreFalls = isYes(get(form, 'suppTwoOrMoreFalls3Years'));
    const backwardsFall = isYes(get(form, 'suppBackwardsFall11'));
    const fracture = isYes(get(form, 'suppFracture11'));
    const headInjury = isYes(get(form, 'suppHeadInjury11'));
    const lyingOnFloor = isYes(get(form, 'suppLyingOnFloor11'));

    if (twoOrMoreFalls && (backwardsFall || fracture || headInjury || lyingOnFloor)) {
      const issues: string[] = [];
      const refs: string[] = ['suppTwoOrMoreFalls3Years'];

      if (backwardsFall) {
        issues.push('backwards fall');
        refs.push('suppBackwardsFall11');
      }
      if (fracture) {
        issues.push('fracture');
        refs.push('suppFracture11');
      }
      if (headInjury) {
        issues.push('head injury');
        refs.push('suppHeadInjury11');
      }
      if (lyingOnFloor) {
        issues.push('unable to get up from floor');
        refs.push('suppLyingOnFloor11');
      }

      return {
        ok: false,
        severity: 'high-confidence',
        code: 'MULTIPLE_FALLS_DECLINE_RISK',
        message: `Applicant had 2+ falls in past 3 years with complications: ${issues.join(', ')}; this is typically an automatic decline per Lincoln underwriting guidelines.`,
        refs,
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R31: Recent cardiac events eligibility risk
const ruleRecentCardiacEventsEligibilityRisk: Rule = {
  id: 'recent.cardiac.events.eligibility.risk',
  check: (form): RuleResult => {
    const heartAttack = isYes(get(form, 'suppHeartAttack13'));
    const stroke = isYes(get(form, 'suppStrokeTIA13'));

    if (heartAttack || stroke) {
      const issues: string[] = [];
      const refs: string[] = [];

      if (heartAttack) {
        issues.push('heart attack');
        refs.push('suppHeartAttack13');
      }
      if (stroke) {
        issues.push('stroke/TIA');
        refs.push('suppStrokeTIA13');
      }

      return {
        ok: false,
        severity: 'high-confidence',
        code: 'RECENT_CARDIAC_EVENT_DECLINE_RISK',
        message: `Applicant diagnosed with ${issues.join(' or ')} in past 6 months; this is typically an automatic decline per Lincoln underwriting guidelines.`,
        refs,
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R32: Current/uncompleted physical therapy eligibility risk
const rulePhysicalTherapyEligibilityRisk: Rule = {
  id: 'physical.therapy.eligibility.risk',
  check: (form): RuleResult => {
    const currentTherapy = isYes(get(form, 'suppPhysicalTherapyCurrent'));
    const advisedNotCompleted = isYes(get(form, 'suppPhysicalTherapyAdvisedNotCompleted'));

    if (currentTherapy || advisedNotCompleted) {
      const issues: string[] = [];
      const refs: string[] = [];

      if (currentTherapy) {
        issues.push('currently in physical therapy');
        refs.push('suppPhysicalTherapyCurrent');
      }
      if (advisedNotCompleted) {
        issues.push('advised to have physical therapy but not completed');
        refs.push('suppPhysicalTherapyAdvisedNotCompleted');
      }

      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'PHYSICAL_THERAPY_RISK',
        message: `Applicant ${issues.join(' or ')}; this may result in decline or postponement per Lincoln underwriting guidelines.`,
        refs,
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R33: Recent opioid prescription eligibility risk
const ruleOpioidPrescriptionEligibilityRisk: Rule = {
  id: 'opioid.prescription.eligibility.risk',
  check: (form): RuleResult => {
    if (isYes(get(form, 'suppOpioidRxPast3Months'))) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'OPIOID_PRESCRIPTION_RISK',
        message:
          'Applicant has opioid prescription in past 3 months; this may result in decline or require additional underwriting per Lincoln guidelines.',
        refs: ['suppOpioidRxPast3Months'],
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// ---------- LTC PERSONAL WORKSHEET RULES ----------------------------

// R34: LTC premium amount required based on premium type
const ruleLTCPremiumAmountRequired: Rule = {
  id: 'ltc.premium.amount.required',
  check: (form): RuleResult => {
    const premiumType = getStr(form, 'ltcPremiumType');
    const errs: string[] = [];
    const refs: string[] = [];

    if (premiumType === 'single') {
      const amount = getStr(form, 'ltcSinglePremiumAmount');
      if (!amount) {
        errs.push('Single premium amount is required when premium type is Single.');
        refs.push('ltcSinglePremiumAmount');
      }
    }

    if (premiumType === 'flexible') {
      const initial = getStr(form, 'ltcFlexiblePremiumInitial');
      const recurring = getStr(form, 'ltcFlexiblePremiumRecurring');

      if (!initial) {
        errs.push('Flexible premium initial amount is required when premium type is Flexible.');
        refs.push('ltcFlexiblePremiumInitial');
      }

      if (!recurring) {
        errs.push('Flexible premium recurring amount is required when premium type is Flexible.');
        refs.push('ltcFlexiblePremiumRecurring');
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'LTC_PREMIUM_AMOUNT_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'ltcPremiumType'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R35: LTC income resource other specify required
const ruleLTCIncomeResourceOtherRequired: Rule = {
  id: 'ltc.income.resource.other.required',
  when: form => {
    const resources = get(form, 'ltcIncomeResourceSelection');
    if (Array.isArray(resources)) {
      return resources.some(r => String(r).toLowerCase().includes('other'));
    }
    return false;
  },
  check: (form): RuleResult => {
    const other = getStr(form, 'ltcIncomeResourceOther');

    if (!other) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'LTC_INCOME_RESOURCE_OTHER_REQUIRED',
        message: 'Please specify other income resources when "Other" is selected.',
        refs: ['ltcIncomeResourceOther', 'ltcIncomeResourceSelection'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R36: LTC pay future costs other specify required
const ruleLTCPayFutureCostsOtherRequired: Rule = {
  id: 'ltc.pay.future.costs.other.required',
  when: form => {
    const payCosts = get(form, 'ltcPayFutureCosts');
    if (Array.isArray(payCosts)) {
      return payCosts.some(c => String(c).toLowerCase().includes('other'));
    }
    return false;
  },
  check: (form): RuleResult => {
    const other = getStr(form, 'ltcPayFutureCostsOther');

    if (!other) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'LTC_PAY_FUTURE_COSTS_OTHER_REQUIRED',
        message: 'Please specify other method to pay for future costs when "Other" is selected.',
        refs: ['ltcPayFutureCostsOther', 'ltcPayFutureCosts'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- EXPORT ------------------------------------------------------------

export const lincolnAllStatesRules: Rule[] = [
  // Coversheet Information
  rulePartnerCaseNameRequired,
  ruleFinancialProfessionalEmailFormat,

  // Proposed Insured
  ruleMailingAddressRequired,
  ruleProposedInsuredContactFormat,
  ruleSpouseNameRequired,

  // Owner Information
  ruleOwnerIndividualNameRequired,
  ruleTrustEntityOwnerNameRequired,
  ruleOwnerContactFormat,

  // Beneficiary Information
  ruleBeneficiaryIndividualNameRequired,
  ruleBeneficiaryEntityNameRequired,
  ruleBeneficiaryPhoneFormat,

  // Payor Information
  rulePayorIndividualNameRequired,
  rulePayorEntityNameRequired,
  rulePayorAddressRequired,

  // Existing/Pending Insurance
  ruleExistingInsuranceActionDetailsRequired,

  // Protection Against Unintended Lapse
  ruleThirdPartyDetailsRequired,
  ruleThirdPartyAddressRequired,

  // Inflation Protection
  ruleInflationProtectionCompoundPercentageRequired,

  // Agent/Financial Professional
  ruleAgentRelationshipDetailsRequired,
  ruleEnglishCompletionExplanationRequired,
  ruleAgentHierarchyChangeDescriptionRequired,

  // Medical Information
  ruleHeightWeightSanity,
  rulePhysicianInformationRequired,

  // Medical Supplement - Insurability Risks
  ruleMajorConditionsEligibilityRisk,
  ruleAssistiveDevicesEligibilityRisk,
  ruleADLDisorderEligibilityRisk,
  ruleSSDIEligibilityRisk,
  ruleAIDSDiagnosisEligibilityRisk,
  ruleRecentCancerEligibilityRisk,
  ruleMultipleFallsEligibilityRisk,
  ruleRecentCardiacEventsEligibilityRisk,
  rulePhysicalTherapyEligibilityRisk,
  ruleOpioidPrescriptionEligibilityRisk,

  // LTC Personal Worksheet
  ruleLTCPremiumAmountRequired,
  ruleLTCIncomeResourceOtherRequired,
  ruleLTCPayFutureCostsOtherRequired,
];

export function evaluateLincolnNigo(form: PolicyFormSubmissionData, state?: string): RuleResult[] {
  return lincolnAllStatesRules
    .filter(r => !r.states || !state || r.states.includes(state))
    .filter(r => (r.when ? r.when(form) : true))
    .map(r => {
      try {
        return r.check(form);
      } catch (e) {
        return {
          ok: false,
          severity: 'high-confidence' as const,
          code: 'RULE_RUNTIME',
          message: `Rule ${r.id} failed: ${String(e)}`,
          category: 'general' as const,
        };
      }
    })
    .filter(res => !res.ok);
}
